import { FormulaEditor, useEditorParseData } from 'components/FormulaEditor';
import { ModalDialog } from 'components/ModalDialog';
import { WorkOrderPriceTypeEnum } from 'gql/graphql';
import { cnMixSpace } from 'uikit';

import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { uerActKC2VolumeVariableSelectionItemsGql } from './actFormulaQueries';
import { getTooltipMessages } from './helpers';
import { useActFormulaHelpers, useSetActFormulaValue } from './hooks';

const TOOLTIP_TITLE = 'Формула расчета';

export const ActFormulaModal = ({
    onClose,
    actId,
    priceId,
    itemType,
    refreshDataHandler,
}: {
    onClose: () => void;
    actId: number;
    priceId: number;
    itemType: WorkOrderPriceTypeEnum | undefined | null;
    refreshDataHandler?: (reloadRowsIds?: number[] | null) => void;
}) => {
    const { control, setValue, handleSubmit } = useForm({});

    const selectionListVars = useMemo(
        () => ({
            actKC2Id: actId,
            workOrderPriceId: priceId,
        }),
        [actId, priceId],
    );

    const {
        isLoading,
        sourceDataGetter,
        dataObjModifier,
        getSelectItemLabel,
        patchedFormula,
        isDataSetCompleted,
        serializeStringForBackend,
    } = useActFormulaHelpers(selectionListVars);

    useEffect(() => {
        if (isDataSetCompleted) {
            setValue('actFormula', patchedFormula);
        }
    }, [isDataSetCompleted, patchedFormula, setValue]);

    const onSuccess = useCallback(
        (reloadRowsIds?: number[] | null) => {
            refreshDataHandler?.(reloadRowsIds);
            onClose();
        },
        [onClose, refreshDataHandler],
    );

    const { setValueHandler, isCommiting } = useSetActFormulaValue({
        variables: selectionListVars,
        onSuccess,
    });

    const { sourceDataForEditor } = useEditorParseData({
        withEffect: false,
        sourceDataGetter,
    });

    const isEmptyVariablesData = useMemo(() => !Object.keys(sourceDataForEditor).length, [sourceDataForEditor]);

    const { tooltipMessage, placeholderMessage } = useMemo(() => {
        if (itemType === WorkOrderPriceTypeEnum.Uer) {
            return getTooltipMessages(isEmptyVariablesData, true);
        }

        if (
            itemType &&
            [
                WorkOrderPriceTypeEnum.MtrCustomerEquipment,
                WorkOrderPriceTypeEnum.MtrCustomerMaterial,
                WorkOrderPriceTypeEnum.MtrContractorMaterial,
                WorkOrderPriceTypeEnum.MtrContractorEquipment,
            ].includes(itemType)
        ) {
            return getTooltipMessages(isEmptyVariablesData, false);
        }

        return {
            tooltipMessage: '',
            placeholderMessage: '',
        };
    }, [isEmptyVariablesData, itemType]);

    const submitHandler = useCallback(() => {
        handleSubmit((i) => {
            if (!i.actFormula) {
                setValueHandler(null);
            } else {
                setValueHandler(serializeStringForBackend(i.actFormula));
            }
        })();
    }, [handleSubmit, serializeStringForBackend, setValueHandler]);

    return (
        <ModalDialog
            title="Ввод формулы"
            isOpen
            onClose={onClose}
            bodyWidth={600}
            isLoading={isLoading}
            isCommit={isCommiting}
            onConfirm={submitHandler}
            isOnClickOutsideDisable
        >
            <div
                className={cnMixSpace({
                    p: 'xl',
                })}
            >
                <FormulaEditor
                    control={control}
                    name="actFormula"
                    getObjectSourceQuery={uerActKC2VolumeVariableSelectionItemsGql}
                    queryVariables={selectionListVars}
                    sourceDataGetter={sourceDataGetter}
                    dataObjModifier={dataObjModifier}
                    getSelectItemLabel={getSelectItemLabel}
                    tooltipTitle={TOOLTIP_TITLE}
                    placeholderMessage={placeholderMessage}
                    tooltipMessage={tooltipMessage}
                    isDotNotationVariables={false}
                    isAvailableWithEmptyVariables
                />
            </div>
        </ModalDialog>
    );
};

ActFormulaModal.displayName = 'ActFormulaModal';
